import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, UserPlus, Truck, PackageCheck, FileCheck, CircleCheck, AlertTriangle, DollarSign, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useFreight, useTransitionFreight } from "@/hooks/useFreights";
import { FreightStatusBadge } from "@/components/ui/freight-status-badge";
import { RepaymentStatusBadge } from "@/components/ui/repayment-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { AssignFreightModal } from "./AssignFreightModal";
import { EditFreightFinanceModal } from "./EditFreightFinanceModal";
import { FreightReceiveModal } from "./FreightReceiveModal";
import { usePermissions } from "@/hooks/usePermissions";
import { formatFreightCode } from "@/lib/utils";
import type { FreightStatus, Repayment } from "@/types/freight";

const RECEIVABLE_STATUSES = new Set<FreightStatus>(["DELIVERED", "PROOF_RECEIVED", "COMPLETED"]);

type TransitionAction = "start" | "deliver" | "proof" | "complete";

const TRANSITION_CONFIG: Record<
  string,
  {
    action: TransitionAction;
    label: string;
    icon: React.ElementType;
    description: string;
    successMsg: string;
  }
> = {
  ASSIGNED: {
    action: "start",
    label: "Iniciar Transporte",
    icon: Truck,
    description: "Confirma que o motorista coletou a carga e está em trânsito. Esta ação não pode ser desfeita.",
    successMsg: "Transporte iniciado!",
  },
  IN_TRANSIT: {
    action: "deliver",
    label: "Registrar Entrega",
    icon: PackageCheck,
    description: "Confirma que a carga foi entregue no destino. Esta ação não pode ser desfeita.",
    successMsg: "Entrega registrada!",
  },
  DELIVERED: {
    action: "proof",
    label: "Confirmar Canhoto",
    icon: FileCheck,
    description:
      "Confirma que a empresa recebeu o canhoto (comprovante de entrega assinado) do motorista. Isso libera o pagamento do saldo. Esta ação não pode ser desfeita.",
    successMsg: "Canhoto confirmado! Saldo liberado para pagamento.",
  },
  PROOF_RECEIVED: {
    action: "complete",
    label: "Concluir Frete",
    icon: CircleCheck,
    description: "Conclui a operação do frete. Esta ação não pode ser desfeita.",
    successMsg: "Frete concluído!",
  },
};

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || "—"}</dd>
    </div>
  );
}

function formatMoney(value: string | number | null) {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

const BANK_ACCOUNT_TYPE_LABEL: Record<string, string> = {
  corrente: "Conta corrente",
  poupanca: "Conta poupança",
};

/** Linha de dado com botão de copiar — usada para a chave PIX. */
function CopyableRow({ label, value }: Readonly<{ label: string; value: string }>) {
  function handleCopy() {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success("Copiado!"))
      .catch(() => toast.error("Não foi possível copiar."));
  }
  return (
    <div className="col-span-2 sm:col-span-3">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 flex items-center gap-2">
        <span className="text-sm font-medium text-foreground break-all">{value}</span>
        <Button variant="ghost" size="icon-sm" onClick={handleCopy} title="Copiar">
          <Copy size={14} />
        </Button>
      </dd>
    </div>
  );
}

const REPAYMENT_LABEL: Record<string, string> = {
  ADVANCE: "Adiantamento",
  BALANCE: "Saldo",
};

function RepaymentRow({ repayment }: Readonly<{ repayment: Repayment }>) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="flex items-center gap-2 text-sm text-foreground">
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
          R-{repayment.code}
        </span>
        {REPAYMENT_LABEL[repayment.type] ?? repayment.type}
      </span>
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground">
          {formatMoney(repayment.amount)}
        </span>
        <RepaymentStatusBadge status={repayment.status} />
      </div>
    </div>
  );
}

export function FreightDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: freight, isLoading } = useFreight(id ?? "");
  const { mutate: transition, isPending: isTransitioning } = useTransitionFreight(id ?? "");
  const [assignOpen, setAssignOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<TransitionAction | null>(null);
  const { has } = usePermissions();
  const isOperator = !has("freight.set_value");
  const canReceive = has("invoice.pay");

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  if (!freight) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Frete não encontrado.</p>
      </div>
    );
  }

  const fv = freight.freightValue ? Number.parseFloat(freight.freightValue) : null;
  const dv = freight.driverValue ? Number.parseFloat(freight.driverValue) : null;
  const margin = !isOperator && fv !== null && dv !== null ? fv - dv : null;
  const marginPct = margin !== null && fv !== null && fv > 0 ? ((margin / fv) * 100).toFixed(1) : null;

  const vehicleLabel = freight.vehicle
    ? [freight.vehicle.plate, freight.vehicle.brand, freight.vehicle.model]
        .filter(Boolean)
        .join(" — ")
    : null;

  const transitionConfig = TRANSITION_CONFIG[freight.status];
  const activeConfirm = confirmAction
    ? Object.values(TRANSITION_CONFIG).find((c) => c.action === confirmAction) ?? null
    : null;

  const isPaid = freight.invoice?.status === "PAID";
  const canShowReceive =
    canReceive &&
    !isPaid &&
    !freight.invoiceId &&
    RECEIVABLE_STATUSES.has(freight.status as FreightStatus) &&
    fv !== null &&
    fv > 0;

  const missingForCompletion: string[] = [];
  if (freight.status === "PROOF_RECEIVED") {
    if (!fv || fv <= 0) missingForCompletion.push("valor do frete");
    if (!dv || dv <= 0) missingForCompletion.push("valor do motorista");
  }
  const blockCompletion =
    transitionConfig?.action === "complete" && missingForCompletion.length > 0;

  function handleTransitionConfirm() {
    if (!confirmAction) return;
    transition(confirmAction, {
      onSuccess: () => {
        const cfg = Object.values(TRANSITION_CONFIG).find((c) => c.action === confirmAction);
        toast.success(cfg?.successMsg ?? "Frete atualizado!");
        setConfirmAction(null);
      },
      onError: () => {
        toast.error("Erro ao atualizar frete. Tente novamente.");
        setConfirmAction(null);
      },
    });
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate("/freights")}>
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs font-medium text-foreground">
              {formatFreightCode(freight.code)}
            </span>
            <h1 className="text-xl font-semibold text-foreground truncate">
              {freight.originCity}/{freight.originState} →{" "}
              {freight.destinationCity}/{freight.destinationState}
            </h1>
            <FreightStatusBadge status={freight.status as FreightStatus} />
            {isPaid && (
              <Badge tone="success" icon={<CheckCircle2 size={11} />}>
                Recebido
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">{freight.company.tradeName}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canShowReceive && (
            <Button size="sm" onClick={() => setReceiveOpen(true)}>
              <CheckCircle2 size={14} />
              Receber pagamento
            </Button>
          )}
          {freight.status === "PENDING" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/freights/${freight.id}/edit`)}
              >
                <Pencil size={14} />
                Editar
              </Button>
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                <UserPlus size={14} />
                Atribuir
              </Button>
            </>
          )}
          {!isOperator && freight.status !== "PENDING" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFinanceOpen(true)}
            >
              <DollarSign size={14} />
              Editar valores
            </Button>
          )}
          {transitionConfig && (
            <Button
              size="sm"
              onClick={() => setConfirmAction(transitionConfig.action)}
              disabled={blockCompletion}
              title={
                blockCompletion
                  ? `Falta preencher: ${missingForCompletion.join(", ")}`
                  : undefined
              }
            >
              <transitionConfig.icon size={14} />
              {transitionConfig.label}
            </Button>
          )}
        </div>
      </div>

      {blockCompletion && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-amber-900">
              Frete pronto para concluir, mas faltam dados.
            </p>
            <p className="mt-0.5 text-amber-800">
              Preencha {missingForCompletion.join(" e ")} antes de concluir.
            </p>
          </div>
          {!isOperator && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFinanceOpen(true)}
            >
              <DollarSign size={14} />
              Editar valores
            </Button>
          )}
        </div>
      )}

      <div className="space-y-5 rounded-lg border border-border p-5">
        {/* Carga */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-foreground">Carga</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="col-span-2 sm:col-span-3">
              <DetailRow label="Descrição" value={freight.cargoDescription} />
            </div>
            <DetailRow label="Tipo" value={freight.cargoType} />
            <DetailRow
              label="Peso (kg)"
              value={
                freight.cargoWeight
                  ? Number.parseFloat(freight.cargoWeight).toLocaleString("pt-BR")
                  : null
              }
            />
          </dl>
        </div>

        {/* Documentos fiscais */}
        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">
            Documentos fiscais
          </h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="Número do CT-e" value={freight.cteNumber} />
            <DetailRow
              label="Número da NF-e (entrega)"
              value={freight.deliveryNfeNumber}
            />
          </dl>
        </div>

        {/* Valores */}
        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Valores</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {!isOperator && (
              <DetailRow label="Valor do frete" value={formatMoney(freight.freightValue)} />
            )}
            <DetailRow label="Valor do motorista" value={formatMoney(freight.driverValue)} />
            <DetailRow
              label="% Adiantamento"
              value={freight.driverValue ? `${Number.parseFloat(freight.advancePercent)}%` : null}
            />
            {margin !== null && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs font-medium text-muted-foreground">Margem</dt>
                <dd className={`mt-0.5 text-sm font-medium ${margin >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {formatMoney(margin)} ({marginPct}%)
                </dd>
              </div>
            )}
            {freight.invoice && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs font-medium text-muted-foreground">Pagamento</dt>
                <dd className="mt-0.5 text-sm text-foreground">
                  {isPaid ? "Recebido" : "Aguardando"} · Fatura {freight.invoice.invoiceNumber}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Atribuição */}
        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Atribuição</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="Motorista" value={freight.driver?.name} />
            <DetailRow label="Veículo" value={vehicleLabel} />
          </dl>
        </div>

        {/* Dados de recebimento do motorista */}
        {freight.driver && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">
              Dados de recebimento
            </h2>
            {freight.driver.pixKey ? (
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <CopyableRow label="Chave PIX" value={freight.driver.pixKey} />
              </dl>
            ) : freight.driver.bankAccount || freight.driver.bankName ? (
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DetailRow label="Banco" value={freight.driver.bankName} />
                <DetailRow label="Agência" value={freight.driver.bankAgency} />
                <DetailRow label="Conta" value={freight.driver.bankAccount} />
                <DetailRow
                  label="Tipo"
                  value={
                    freight.driver.bankAccountType
                      ? BANK_ACCOUNT_TYPE_LABEL[freight.driver.bankAccountType] ??
                        freight.driver.bankAccountType
                      : null
                  }
                />
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">
                Motorista sem chave PIX ou dados bancários cadastrados.
              </p>
            )}
          </div>
        )}

        {/* Repasses */}
        {freight.repayments.length > 0 && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">Repasses</h2>
            <div className="divide-y divide-border">
              {freight.repayments.map((r) => (
                <RepaymentRow key={r.id} repayment={r} />
              ))}
            </div>
          </div>
        )}

        {/* Datas */}
        <div className="border-t border-border pt-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Datas</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <DetailRow label="Criado em" value={formatDate(freight.createdAt)} />
            <DetailRow label="Atribuído em" value={formatDate(freight.assignedAt)} />
            <DetailRow label="Coleta" value={formatDate(freight.pickedUpAt)} />
            <DetailRow label="Entregue em" value={formatDate(freight.deliveredAt)} />
            <DetailRow
              label="Canhoto recebido em"
              value={formatDate(freight.proofReceivedAt)}
            />
            <DetailRow label="Concluído em" value={formatDate(freight.completedAt)} />
          </dl>
        </div>

        {/* Observações */}
        {freight.notes && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-2 text-sm font-medium text-foreground">Observações</h2>
            <p className="text-sm text-foreground whitespace-pre-wrap">{freight.notes}</p>
          </div>
        )}
      </div>

      {/* Modal de edição de valores */}
      <EditFreightFinanceModal
        freightId={freight.id}
        initialFreightValue={fv}
        initialDriverValue={dv}
        initialAdvancePercent={
          freight.advancePercent ? Number.parseFloat(freight.advancePercent) : null
        }
        initialNotes={freight.notes}
        driverFieldsLocked={freight.repayments.some((r) => r.status === "PAID")}
        open={financeOpen}
        onClose={() => setFinanceOpen(false)}
      />

      {/* Modal de atribuição */}
      <AssignFreightModal
        freightId={freight.id}
        freightValue={fv}
        initialDriverValue={dv}
        initialAdvancePercent={
          freight.advancePercent ? Number.parseFloat(freight.advancePercent) : null
        }
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSuccess={() => toast.success("Motorista atribuído com sucesso!")}
      />

      {/* Modal de recebimento */}
      <FreightReceiveModal
        freight={freight}
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
      />

      {/* Dialog de confirmação de transição */}
      <ConfirmDialog
        open={confirmAction !== null}
        title={activeConfirm?.label ?? "Confirmar"}
        description={activeConfirm?.description ?? "Deseja confirmar esta ação?"}
        confirmLabel={activeConfirm?.label ?? "Confirmar"}
        onConfirm={handleTransitionConfirm}
        onCancel={() => setConfirmAction(null)}
        loading={isTransitioning}
      />
    </div>
  );
}
